import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { getText } from 'services/languageService';
import {
  setBackdropLoadingAction,
  setExportingStatsAction,
} from 'store/actions/mainPage';
import { ExportStatProps } from 'types/mainPage';
import { exportDataLog, exportFile } from 'utils/exportFile';
import { exportToPDF } from 'components/TablesTab/ExportTable/exportToPDF';
import { Box, List, ListItem, Typography } from '@mui/material';
import { formatNumber } from 'store/actions/mainPage/constant';

export const TableForExport: React.FC<ExportStatProps> = ({ setOpen }) => {
  const {
    statisticsData,
    isLogged,
    exportingStats: exporting,
    exportingExt: ext,
    lang,
  } = useAppSelector((state) => state.mainPage);
  const tableRef = useRef(null);
  const dispatch = useAppDispatch();

  if (!statisticsData) {
    return null;
  }
  const UNIT_NAME = getText('UNIT_NAME');

  const { data, export: exportInfo } = statisticsData;
  const fz = innerWidth > 5000 ? '8rem' : innerWidth > 3000 ? '3rem' : '1rem';

  useEffect(() => {
    const { filename: fileTitle, unit, key, value } = statisticsData.export;
    const _data = statisticsData.data?.map((item) => ({
      [key]: item.key,
      [value]: formatNumber(item.paramValue, 'ru'),
      [UNIT_NAME]: unit || item.unit,
    }));

    const _data_xlsx = statisticsData.data?.map((item) => ({
      [key]: item.key,
      [value]: +item.paramValue,
      // [value]: formatNumber(item.paramValue, 'ru'),
      [UNIT_NAME]: unit || item.unit,
    }));

    const tableData =
      ext === 'xlsx'
        ? [
            { [key]: fileTitle, [value]: '', [UNIT_NAME]: '' },
            {
              [key]: key,
              [value]: value,
              [UNIT_NAME]: UNIT_NAME,
            },
            ..._data_xlsx,
          ]
        : [];

    if (ext === 'pdf') {
      const pdfData = [
        {
          [key]: key,
          [value]: value,
          [UNIT_NAME]: UNIT_NAME,
        },
        ..._data,
      ].map((i) => Object.values(i));
      exportToPDF(pdfData, fileTitle);
      exportDataLog(isLogged, fileTitle, ext, 'tables').finally(() => {
        dispatch(setExportingStatsAction(false, ''));
      });
    } else {
      exportFile(tableRef.current, fileTitle, ext, tableData)
        .then(() => exportDataLog(isLogged, fileTitle, ext, 'tables'))
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          dispatch(setExportingStatsAction(false, ''));
        });
    }

    return () => {
      const timeout = tableData.length * 2;
      setTimeout(() => {
        dispatch(setBackdropLoadingAction(false));
        setOpen(false);
      }, timeout);
    };
  }, [exporting]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        position: 'fixed',
        left: '-9999px',
        bottom: '-9999px',
        minWidth: 400,
      }}
    >
      <List ref={tableRef} sx={{ flex: 1, p: 3 }}>
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: fz,
            fontWeight: 700,
            maxWidth: 400,
            mx: 'auto',
          }}
        >
          {statisticsData?.export.filename}
        </Typography>
        {data.map((item, index) => {
          return (
            <ListItem
              key={item.key + index}
              sx={{
                display: 'flex',
              }}
            >
              <Typography sx={{ flexBasis: '50%', fontSize: fz }}>
                {item.key}
              </Typography>
              <Typography
                style={{ flexBasis: '50%', textAlign: 'end', fontSize: fz }}
              >
                {formatNumber(item.paramValue, lang)}{' '}
                {exportInfo.unit || item.unit}
              </Typography>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};
